import type { Metric } from "web-vitals";

export const WEB_VITALS_EVENT = "$web_vitals";

export interface LandingDocument {
  href: string;
  pathname: string;
}

export type WebVitalsProperties = Record<string, unknown>;

function metricEvent(metric: Metric): Record<string, unknown> {
  return {
    name: metric.name,
    value: metric.value,
    delta: metric.delta,
    rating: metric.rating,
    id: metric.id,
    navigationType: metric.navigationType,
  };
}

export function webVitalsProperties(
  metrics: readonly Metric[],
  landing: LandingDocument,
): WebVitalsProperties {
  const properties: WebVitalsProperties = {
    $current_url: landing.href,
    $pathname: landing.pathname,
  };
  for (const metric of metrics) {
    properties[`$web_vitals_${metric.name}_value`] = metric.value;
    properties[`$web_vitals_${metric.name}_event`] = metricEvent(metric);
    properties.web_vitals_navigation_type = metric.navigationType;
  }
  return properties;
}

export interface WebVitalsReporter {
  record: (metric: Metric) => void;
  flush: () => void;
}

export function createWebVitalsReporter(
  landing: LandingDocument,
  send: (properties: WebVitalsProperties) => void,
): WebVitalsReporter {
  const pending = new Map<string, Metric>();
  return {
    record: (metric) => {
      pending.set(metric.name, metric);
    },
    flush: () => {
      if (pending.size === 0) return;
      send(webVitalsProperties([...pending.values()], landing));
      pending.clear();
    },
  };
}

export async function startWebVitalsReporting(
  send: (properties: WebVitalsProperties) => void,
): Promise<void> {
  const reporter = createWebVitalsReporter(
    { href: window.location.href, pathname: window.location.pathname },
    send,
  );
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") reporter.flush();
  });
  try {
    const { onCLS, onFCP, onINP, onLCP, onTTFB } = await import("web-vitals");
    onTTFB(reporter.record);
    onFCP(reporter.record);
    onLCP(reporter.record);
    onCLS(reporter.record);
    onINP(reporter.record);
  } catch {}
}
